import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { STOTRAS, loadStotra } from "@/data/stotras";
import { useSeo } from "@/lib/seo";
import { StotraHeader } from "./StotraHub";
import { WhatsAppShare } from "@/components/ShareButtons";

const RECITE_GUIDE = {
  sahasranama: "It is traditionally recited as morning or evening parayana. Fridays, Ekadashi and festival days are considered especially auspicious for a full recitation.",
  ashtottara: "It is recited during daily pooja or as archana — offering kumkuma or a flower with each of the 108 names.",
  nitya: "It is meant for regular recitation — woven into the daily morning or evening routine as nitya parayana.",
  habba: "It is recited on the festival day and during the related vrata, and is equally suitable for daily parayana.",
};

const VerseRow = ({ v, i }) => (
  <div className="border-b border-dashed border-[#A8722A]/25 py-3" data-testid={`verse-row-${i}`}>
    <div className="grid grid-cols-1 gap-1 md:grid-cols-2 md:gap-6">
      <p className="whitespace-pre-line font-baloo text-[17px] leading-relaxed text-[#3B221B]">{v.kn}</p>
      <p className="whitespace-pre-line font-mukta text-[15px] leading-relaxed text-[#6B5044]">{v.en}</p>
    </div>
    {v.meaning && (
      <div className="mt-2 rounded-[10px] border border-[#3E6B4F]/20 px-3.5 py-2.5" style={{ backgroundColor: "rgba(62,107,79,0.06)" }} data-testid={`verse-meaning-${i}`}>
        <p className="font-mukta text-[13px] font-semibold uppercase tracking-wider text-[#3E6B4F]">ಅರ್ಥ · Meaning</p>
        {v.meaning.kn && <p className="mt-1 font-baloo text-[14px] leading-relaxed text-[#3B221B]/85">{v.meaning.kn}</p>}
        {v.meaning.en && <p className="mt-1 font-mukta text-[13.5px] leading-relaxed text-[#6B5044]">{v.meaning.en}</p>}
      </div>
    )}
  </div>
);

export default function StotraPage() {
  const { slug } = useParams();
  const meta = STOTRAS.find((s) => s.slug === slug);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    if (!meta) return;
    loadStotra(slug).then(setData).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [slug, meta]);

  const idx = STOTRAS.findIndex((s) => s.slug === slug);
  const prev = idx > 0 ? STOTRAS[idx - 1] : null;
  const next = idx >= 0 && idx < STOTRAS.length - 1 ? STOTRAS[idx + 1] : null;
  const related = useMemo(() => STOTRAS.filter((s) => s.slug !== slug && meta && (s.deity === meta.deity || s.category === meta.category)).slice(0, 3), [slug, meta]);

  const faqs = useMemo(() => meta ? [
    { q: `What is ${meta.titleEn}?`, a: `${meta.titleEn} (${meta.titleKn}) — ${meta.blurb}` },
    { q: `When should ${meta.titleEn} be recited?`, a: RECITE_GUIDE[meta.category] || RECITE_GUIDE.nitya },
    { q: `Is ${meta.titleEn} available in Kannada?`, a: `Yes — this page gives the complete ${meta.titleEn} in Kannada script with English transliteration side by side, free to read and share.` },
  ] : [], [meta]);

  useSeo({
    title: meta ? `${meta.titleEn} — ${meta.titleKn} | Kannada & English | PurohithConnect` : "Stotra not found",
    description: meta ? `${meta.titleEn} (${meta.titleKn}) — complete text in Kannada script with English transliteration. ${meta.blurb}` : "",
    canonicalPath: `/stotras/${slug}`,
    jsonLd: useMemo(() => meta && [
      {
        "@context": "https://schema.org", "@type": "Article",
        headline: meta.titleEn, alternativeHeadline: meta.titleKn, inLanguage: ["kn", "en"],
        image: `https://purohithconnect.com${meta.img}`,
        publisher: { "@type": "Organization", name: "PurohithConnect" },
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Stotra Library", item: "https://purohithconnect.com/stotras" },
          { "@type": "ListItem", position: 2, name: meta.titleEn, item: `https://purohithconnect.com/stotras/${slug}` },
        ],
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: faqs.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
      },
    ], [meta, slug, faqs]),
  });

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#FBF6EC]">
        <StotraHeader />
        <div className="py-24 text-center font-mukta text-[#6B5044]">
          Stotra not found. <Link to="/stotras" className="text-[#B23A2B] underline">Back to library</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <StotraHeader />
      <main className="mx-auto max-w-4xl px-4 pb-20 md:px-8">
        <nav className="pt-5 font-mukta text-sm text-[#6B5044]" aria-label="Breadcrumb">
          <Link to="/stotras" className="text-[#A8722A] hover:underline" data-testid="stotra-breadcrumb">← Stotra Library</Link>
        </nav>
        <article className="mt-4">
          <div className="overflow-hidden rounded-[14px] border border-[#A8722A]/30">
            <img src={meta.img} alt={meta.titleEn} className="h-64 w-full object-cover object-top md:h-80" />
          </div>
          <p className="mt-6 text-center font-baloo text-sm text-[#B23A2B]">|| ಶ್ರೀ ||</p>
          <h1 className="mt-1 text-center font-eczar text-3xl font-bold md:text-4xl" data-testid="stotra-title-en">{meta.titleEn}</h1>
          <p className="mt-1 text-center font-baloo text-xl text-[#A8722A]" data-testid="stotra-title-kn">{meta.titleKn}</p>
          <div className="mt-4 flex justify-center">
            <WhatsAppShare text={`${meta.titleKn} — ${meta.titleEn} | Complete text in Kannada & English with meaning: https://purohithconnect.com/stotras/${slug} 🙏`} />
          </div>

          {err && <p className="mt-10 text-center font-mukta text-[#6B5044]">Could not load this stotra. Please refresh.</p>}
          {!data && !err && <p className="mt-10 text-center font-mukta text-[#6B5044]">Loading…</p>}

          {data && (
            <>
              {data.intro && (
                <div className="mt-6 rounded-[14px] border border-[#A8722A]/25 bg-[#F3EADA]/70 p-5">
                  <p className="font-baloo text-[15px] text-[#3B221B]">{data.intro.kn}</p>
                  <p className="mt-2 font-mukta text-sm text-[#6B5044]">{data.intro.en}</p>
                </div>
              )}
              <div className="mt-4 hidden justify-between border-b-2 border-[#A8722A]/40 pb-2 font-mukta text-xs font-semibold uppercase tracking-widest text-[#A8722A] md:flex">
                <span>ಕನ್ನಡ · Kannada</span><span>English</span>
              </div>
              {data.sections.map((sec, si) => (
                <section key={si} className="mt-2">
                  {sec.heading && (
                    <h2 className="mt-8 rounded-lg bg-[#F3EADA] px-4 py-2 font-eczar text-lg font-semibold text-[#3B221B]">
                      {sec.heading.kn} <span className="font-mukta text-sm font-normal text-[#6B5044]">· {sec.heading.en}</span>
                    </h2>
                  )}
                  {sec.verses.map((v, vi) => <VerseRow key={vi} v={v} i={`${si}-${vi}`} />)}
                </section>
              ))}
              <p className="mt-8 text-center font-baloo text-lg text-[#A8722A]">|| ಶುಭಮಸ್ತು ||</p>
              <p className="mt-4 text-center font-mukta text-xs text-[#6B5044]/70">
                Text compiled from traditional sources for devotional use. Spotted an error? Write to namaste@purohithconnect.com
              </p>
            </>
          )}
        </article>

        <nav className="mt-10 flex items-center justify-between gap-4 font-mukta text-sm">
          {prev ? (
            <Link to={`/stotras/${prev.slug}`} className="flex items-center gap-1.5 text-[#A8722A] hover:underline" data-testid="stotra-prev">
              <ArrowLeft size={15} /> {prev.titleEn}
            </Link>
          ) : <span />}
          {next && (
            <Link to={`/stotras/${next.slug}`} className="flex items-center gap-1.5 text-right text-[#A8722A] hover:underline" data-testid="stotra-next">
              {next.titleEn} <ArrowRight size={15} />
            </Link>
          )}
        </nav>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-eczar text-xl font-semibold">You may also like</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {related.map((s) => (
                <Link key={s.slug} to={`/stotras/${s.slug}`} className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm hover:shadow-md" data-testid={`related-${s.slug}`}>
                  <p className="font-baloo text-sm text-[#A8722A]">{s.titleKn}</p>
                  <p className="font-eczar text-sm font-semibold">{s.titleEn}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <h2 className="font-eczar text-xl font-semibold">Frequently asked · ಪ್ರಶ್ನೋತ್ತರ</h2>
          <div className="mt-3 space-y-3">
            {faqs.map((x, i) => (
              <div key={i} className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4" data-testid={`stotra-faq-${i}`}>
                <p className="font-eczar text-[15px] font-semibold">{x.q}</p>
                <p className="mt-1 font-mukta text-sm text-[#6B5044]">{x.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <footer className="border-t border-[#A8722A]/20 bg-[#F3EADA] py-5 text-center font-mukta text-sm text-[#6B5044]">
        © 2026 PurohithConnect · Built in Bengaluru, Karnataka — for India
      </footer>
    </div>
  );
}
